import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Tv, Save, PhoneCall, Volume2, Play, Palette, ChevronRight } from "lucide-react";
import useTenantPath from "../lib/useTenantPath";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import { useAuth } from "../context/AuthContext";

const DEFAULT_CALL_CFG = {
  announcement_repeats: 1,
  announcement_gap_ms: 500,
  allow_manual_replay: true,
  voice_name: null,
  voice_language: "pt-BR",
  announcement_message_style: "full",
};

const MESSAGE_STYLES = [
  { value: "full", label: "Completa", example: "Carla, cliente aguardando atendimento." },
  { value: "short", label: "Curta", example: "Carla, cliente aguardando." },
  { value: "name_only", label: "Somente nome", example: "Carla." },
];

function buildAnnouncement(style, name) {
  if (style === "name_only") return `${name}.`;
  if (style === "short") return `${name}, cliente aguardando.`;
  return `${name}, cliente aguardando atendimento.`;
}

export default function CategoriesPage() {
  const { user } = useAuth();
  const isMaster = user?.admin_level === "master";
  const tp = useTenantPath();
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [tvPin, setTvPin] = useState("");
  const [tvPinLoading, setTvPinLoading] = useState(false);
  const [callCfg, setCallCfg] = useState(DEFAULT_CALL_CFG);
  const [callCfgLoading, setCallCfgLoading] = useState(false);
  const [voices, setVoices] = useState([]);

  const load = async () => {
    const { data } = await api.get("/categories");
    setCats(data);
  };
  const loadTvPin = async () => {
    if (!isMaster) return;
    try {
      const { data } = await api.get("/settings/tv-pin");
      setTvPin(data.pin || "");
    } catch (e) {}
  };
  const loadCallCfg = async () => {
    try {
      const { data } = await api.get("/settings/calls");
      setCallCfg((c) => ({ ...c, ...data }));
    } catch (e) {}
  };
  useEffect(() => {
    load();
    loadTvPin();
    loadCallCfg();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMaster]);

  // Carrega vozes disponíveis do navegador (pt-BR)
  useEffect(() => {
    if (typeof window === "undefined" || !("speechSynthesis" in window)) return;
    const refresh = () => {
      const all = window.speechSynthesis.getVoices();
      const pt = all.filter((v) => (v.lang || "").toLowerCase().startsWith("pt"));
      setVoices(pt.length ? pt : all);
    };
    refresh();
    window.speechSynthesis.onvoiceschanged = refresh;
    return () => { window.speechSynthesis.onvoiceschanged = null; };
  }, []);

  const saveTvPin = async () => {
    if (!/^\d{4}$/.test(tvPin)) {
      toast.error("PIN deve ter 4 dígitos numéricos");
      return;
    }
    setTvPinLoading(true);
    try {
      await api.put("/settings/tv-pin", { pin: tvPin });
      toast.success("PIN do Painel TV atualizado");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setTvPinLoading(false);
    }
  };

  const saveCallCfg = async () => {
    setCallCfgLoading(true);
    try {
      await api.put("/settings/calls", callCfg);
      toast.success("Configurações de chamada salvas");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCallCfgLoading(false);
    }
  };

  const testVoice = () => {
    if (typeof window === "undefined" || !("speechSynthesis" in window)) {
      toast.error("Este navegador não suporta síntese de voz");
      return;
    }
    try { window.speechSynthesis.cancel(); } catch (e) {}
    const style = callCfg.announcement_message_style || "full";
    const text = buildAnnouncement(style, "Carla");
    const doSpeak = () => {
      const u = new SpeechSynthesisUtterance(text);
      u.lang = callCfg.voice_language || "pt-BR";
      const v = voices.find((x) => x.name === callCfg.voice_name);
      if (v) u.voice = v;
      window.speechSynthesis.speak(u);
    };
    const n = Math.max(1, Math.min(3, parseInt(callCfg.announcement_repeats, 10) || 1));
    doSpeak();
    for (let i = 1; i < n; i++) {
      setTimeout(doSpeak, i * ((parseInt(callCfg.announcement_gap_ms, 10) || 0) + 2000));
    }
  };

  const openNew = () => {
    setForm({ name: "", active: true, queue_mode: "vez", display_order: 0, hide_when_empty: false });
    setEditingId(null);
  };
  const openEdit = (c) => {
    setForm({
      name: c.name,
      active: c.active,
      queue_mode: c.queue_mode || "vez",
      display_order: typeof c.display_order === "number" ? c.display_order : 0,
      hide_when_empty: !!c.hide_when_empty,
    });
    setEditingId(c.id);
  };

  const submit = async () => {
    try {
      if (editingId) {
        await api.put(`/categories/${editingId}`, form);
        toast.success("Categoria atualizada");
      } else {
        await api.post("/categories", form);
        toast.success("Categoria criada");
      }
      setForm(null);
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Excluir categoria ${c.name}?`)) return;
    try {
      await api.delete(`/categories/${c.id}`);
      toast.success("Excluída");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div>
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.25em] text-gray-500">Configurações</div>
        <h1 className="font-display text-3xl sm:text-4xl font-medium tracking-tight">Configurações do estabelecimento</h1>
        <p className="mt-1 text-sm text-gray-500 max-w-2xl">Personalize o funcionamento do FILA de acordo com a operação do seu estabelecimento.</p>
      </div>

      {isMaster && (
        <Link
          to={tp("/personalizacao")}
          data-testid="link-identidade"
          className="mb-6 flex items-center justify-between gap-3 rounded-2xl border border-pink-100 bg-gradient-to-r from-pink-50 to-white p-5 hover:border-pink-300 transition-all"
        >
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-pink-500 text-white">
              <Palette size={18} />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-widest text-gray-500">Identidade</div>
              <div className="font-display text-lg font-semibold tracking-tight">Identidade &amp; Personalização</div>
              <div className="text-xs text-gray-500">Nome exibido no app, no Painel TV e logo do estabelecimento.</div>
            </div>
          </div>
          <ChevronRight size={18} className="text-pink-500" />
        </Link>
      )}

      {isMaster && (
        <div data-testid="tv-pin-card" className="mb-8 rounded-2xl border border-gray-200 bg-white p-5">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-black text-white">
                <Tv size={18} />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-widest text-gray-500">Painel TV</div>
                <div className="font-display text-lg font-semibold tracking-tight">PIN de saída</div>
                <div className="text-xs text-gray-500">Usado para sair do modo Painel TV (4 dígitos)</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                data-testid="input-tv-pin-setting"
                type="text"
                inputMode="numeric"
                maxLength={4}
                value={tvPin}
                onChange={(e) => setTvPin(e.target.value.replace(/\D/g, ""))}
                placeholder="1234"
                className="w-28 rounded-lg border border-gray-200 px-3 py-2 text-center text-lg tracking-[0.4em] outline-none focus:border-black"
              />
              <button
                data-testid="btn-save-tv-pin"
                onClick={saveTvPin}
                disabled={tvPinLoading}
                className="inline-flex items-center gap-2 rounded-full bg-black px-4 py-2 text-xs font-semibold uppercase tracking-widest text-white disabled:opacity-50"
              >
                <Save size={14} /> Salvar PIN
              </button>
            </div>
          </div>
        </div>
      )}

      {isMaster && (
        <div data-testid="call-settings-card" className="mb-8 rounded-2xl border border-gray-200 bg-white p-5">
          <div className="mb-5 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-black text-white">
              <PhoneCall size={18} />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-widest text-gray-500">Configurações</div>
              <div className="font-display text-lg font-semibold tracking-tight">Chamadas de Profissionais</div>
              <div className="text-xs text-gray-500">Voz, repetições e intervalo do aviso reproduzido no Painel TV</div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-widest text-gray-500">Voz da chamada</label>
              <select
                data-testid="select-voice"
                value={callCfg.voice_name || ""}
                onChange={(e) => setCallCfg((c) => ({ ...c, voice_name: e.target.value || null }))}
                className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-black"
              >
                <option value="">Padrão do navegador</option>
                {voices.map((v) => (
                  <option key={`${v.name}-${v.lang}`} value={v.name}>{v.name} — {v.lang}</option>
                ))}
              </select>
              <div className="mt-1 text-[11px] text-gray-500">
                {voices.length === 0 ? "Nenhuma voz PT-BR detectada neste navegador. Use o Painel TV em um dispositivo com voz PT-BR instalada." : `${voices.length} voz(es) disponível(is).`}
              </div>
            </div>

            <div>
              <label className="text-[11px] font-semibold uppercase tracking-widest text-gray-500">Idioma</label>
              <input
                data-testid="input-voice-lang"
                value={callCfg.voice_language || "pt-BR"}
                readOnly
                className="mt-1 w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600"
              />
              <div className="mt-1 text-[11px] text-gray-500">Português (Brasil) — fixo.</div>
            </div>

            <div>
              <label className="text-[11px] font-semibold uppercase tracking-widest text-gray-500">Quantidade de repetições</label>
              <div data-testid="repeats-group" className="mt-1 inline-flex overflow-hidden rounded-full border border-gray-200">
                {[1, 2, 3].map((n) => (
                  <button
                    key={n}
                    type="button"
                    data-testid={`btn-repeats-${n}`}
                    onClick={() => setCallCfg((c) => ({ ...c, announcement_repeats: n }))}
                    className={`px-4 py-1.5 text-xs font-semibold uppercase tracking-widest ${
                      callCfg.announcement_repeats === n ? "bg-black text-white" : "bg-white text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {n}x
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-[11px] font-semibold uppercase tracking-widest text-gray-500">Intervalo entre repetições (ms)</label>
              <input
                data-testid="input-gap-ms"
                type="number"
                min={0}
                max={3000}
                step={100}
                value={callCfg.announcement_gap_ms ?? 500}
                onChange={(e) => setCallCfg((c) => ({ ...c, announcement_gap_ms: parseInt(e.target.value || "0", 10) }))}
                className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-black"
              />
              <div className="mt-1 text-[11px] text-gray-500">Padrão 500 ms. Pausa curta entre as falas para não permitir o receiver reativar a música.</div>
            </div>

            <label className="col-span-1 md:col-span-2 flex items-center gap-2 text-sm text-gray-700">
              <input
                data-testid="toggle-allow-replay"
                type="checkbox"
                checked={!!callCfg.allow_manual_replay}
                onChange={(e) => setCallCfg((c) => ({ ...c, allow_manual_replay: e.target.checked }))}
                className="h-4 w-4 rounded border-gray-300"
              />
              Permitir botão <b>Repetir chamada</b> na recepção enquanto a chamada estiver ativa
            </label>

            <div className="col-span-1 md:col-span-2 border-t border-gray-100 pt-4">
              <label className="block text-[11px] font-semibold uppercase tracking-widest text-gray-500 mb-3">Mensagem falada na chamada</label>
              <div data-testid="msg-style-group" className="space-y-2">
                {MESSAGE_STYLES.map((s) => (
                  <label
                    key={s.value}
                    data-testid={`msg-style-${s.value}`}
                    className={`flex items-start gap-3 cursor-pointer rounded-xl border p-3 transition-colors ${
                      (callCfg.announcement_message_style || "full") === s.value
                        ? "border-pink-500 bg-pink-50/50"
                        : "border-gray-200 bg-white hover:border-pink-200"
                    }`}
                  >
                    <input
                      type="radio"
                      name="msg-style"
                      value={s.value}
                      checked={(callCfg.announcement_message_style || "full") === s.value}
                      onChange={() => setCallCfg((c) => ({ ...c, announcement_message_style: s.value }))}
                      className="mt-0.5 h-4 w-4 accent-pink-500"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-semibold">{s.label}</div>
                      <div className="text-xs text-gray-500 italic">"{s.example}"</div>
                    </div>
                  </label>
                ))}
              </div>
              <p className="mt-2 text-[11px] text-gray-500">O botão <b>Ouvir exemplo</b> abaixo reproduz uma amostra usando o formato e a voz selecionados.</p>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-4">
            <button
              data-testid="btn-test-voice"
              onClick={testVoice}
              className="inline-flex items-center gap-2 rounded-full border border-gray-200 px-4 py-2 text-xs font-medium hover:bg-gray-50"
            >
              <Play size={14} /> Ouvir exemplo
            </button>
            <button
              data-testid="btn-save-call-settings"
              onClick={saveCallCfg}
              disabled={callCfgLoading}
              className="inline-flex items-center gap-2 rounded-full bg-black px-4 py-2 text-xs font-semibold uppercase tracking-widest text-white disabled:opacity-50"
            >
              <Save size={14} /> Salvar configurações
            </button>
          </div>
        </div>
      )}

      <div className="mt-10 mb-4 flex flex-wrap items-end justify-between gap-3 border-t border-gray-100 pt-8">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-gray-500">Seção</div>
          <h2 className="font-display text-2xl sm:text-3xl font-medium tracking-tight">Categorias de atendimento</h2>
          <p className="mt-1 text-sm text-gray-500">Cadastre e organize as categorias utilizadas nas filas.</p>
        </div>
        <button
          data-testid="btn-new-cat"
          onClick={openNew}
          className="inline-flex items-center gap-2 rounded-full bg-black px-4 py-2 text-sm font-semibold text-white hover:-translate-y-[1px] transition-transform"
        >
          <Plus size={16} /> Nova categoria
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        {cats.map((c) => (
          <div key={c.id} data-testid={`cat-card-${c.id}`} className="rounded-2xl border border-gray-200 bg-white p-5">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-[11px] uppercase tracking-widest text-gray-500">Categoria</div>
                <div className="font-display text-xl font-semibold tracking-tight">{c.name}</div>
                <div className={`mt-2 text-xs ${c.active ? "text-green-600" : "text-gray-400"}`}>
                  {c.active ? "Ativa" : "Inativa"}
                </div>
                <div className="mt-1">
                  <span
                    data-testid={`cat-mode-${c.id}`}
                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest ${
                      (c.queue_mode || "vez") === "quantidade"
                        ? "bg-pink-100 text-pink-700"
                        : "bg-gray-100 text-gray-700"
                    }`}
                  >
                    {(c.queue_mode || "vez") === "quantidade" ? "Por quantidade" : "Por vez"}
                  </span>
                  <span data-testid={`cat-order-${c.id}`} className="ml-2 inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-gray-700">
                    Ordem {typeof c.display_order === "number" ? c.display_order : 0}
                  </span>
                  {c.hide_when_empty && (
                    <span data-testid={`cat-hide-empty-${c.id}`} className="ml-2 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-amber-800">
                      Oculta se vazia
                    </span>
                  )}
                </div>
              </div>
              <div className="flex gap-1">
                <button
                  data-testid={`btn-edit-cat-${c.id}`}
                  onClick={() => openEdit(c)}
                  className="rounded-full border border-gray-200 p-2 hover:bg-gray-50"
                >
                  <Pencil size={14} />
                </button>
                <button
                  data-testid={`btn-del-cat-${c.id}`}
                  onClick={() => remove(c)}
                  className="rounded-full border border-gray-200 p-2 hover:bg-red-50 hover:text-red-600"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent data-testid="dialog-cat">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editingId ? "Editar categoria" : "Nova categoria"}
            </DialogTitle>
            <DialogDescription>Categorias representam os serviços oferecidos. Cada uma tem sua própria fila.</DialogDescription>
          </DialogHeader>
          {form && (
            <div className="space-y-4">
              <div>
                <label className="mb-2 block text-xs font-medium uppercase tracking-wider text-gray-500">Nome</label>
                <input
                  data-testid="input-cat-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-base outline-none focus:border-black"
                />
              </div>
              <div>
                <label className="mb-2 block text-xs font-medium uppercase tracking-wider text-gray-500">Modo de operação da fila</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    data-testid="mode-vez"
                    onClick={() => setForm({ ...form, queue_mode: "vez" })}
                    className={`rounded-xl border p-3 text-left transition-colors ${
                      form.queue_mode === "vez"
                        ? "border-black bg-black text-white"
                        : "border-gray-200 bg-white text-gray-700 hover:border-black"
                    }`}
                  >
                    <div className="font-medium">Por vez</div>
                    <div className={`mt-1 text-[10px] leading-tight ${form.queue_mode === "vez" ? "text-white/80" : "text-gray-500"}`}>
                      Rodízio pela ordem da fila
                    </div>
                  </button>
                  <button
                    type="button"
                    data-testid="mode-quantidade"
                    onClick={() => setForm({ ...form, queue_mode: "quantidade" })}
                    className={`rounded-xl border p-3 text-left transition-colors ${
                      form.queue_mode === "quantidade"
                        ? "border-black bg-black text-white"
                        : "border-gray-200 bg-white text-gray-700 hover:border-black"
                    }`}
                  >
                    <div className="font-medium">Por quantidade</div>
                    <div className={`mt-1 text-[10px] leading-tight ${form.queue_mode === "quantidade" ? "text-white/80" : "text-gray-500"}`}>
                      Quem tem menos atendimentos no expediente atual
                    </div>
                  </button>
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  data-testid="input-cat-active"
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => setForm({ ...form, active: e.target.checked })}
                />
                Ativa
              </label>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-semibold uppercase tracking-widest text-gray-500">Ordem de exibição</label>
                  <input
                    data-testid="input-cat-order"
                    type="number"
                    min={0}
                    step={1}
                    value={form.display_order ?? 0}
                    onChange={(e) => setForm({ ...form, display_order: parseInt(e.target.value || "0", 10) })}
                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-black"
                  />
                  <div className="mt-1 text-[11px] text-gray-500">Menor primeiro. Ex.: 1, 2, 3…</div>
                </div>
                <label className="flex items-start gap-2 pt-6 text-sm">
                  <input
                    data-testid="input-cat-hide-empty"
                    type="checkbox"
                    checked={!!form.hide_when_empty}
                    onChange={(e) => setForm({ ...form, hide_when_empty: e.target.checked })}
                    className="mt-1"
                  />
                  <span>
                    Ocultar quando não houver profissional trabalhando
                    <span className="mt-0.5 block text-[11px] text-gray-500">Some da Fila e do Painel TV quando ninguém dessa categoria está em expediente e a fila está vazia.</span>
                  </span>
                </label>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <button onClick={() => setForm(null)} className="rounded-full border border-gray-200 px-4 py-2 text-sm">
              Cancelar
            </button>
            <button
              data-testid="btn-save-cat"
              onClick={submit}
              className="rounded-full bg-black px-4 py-2 text-sm font-semibold text-white"
            >
              Salvar
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
